import { ITeamMember } from '@/@types/TeamMember';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { TeamService } from '@/services/team.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface RemoveMemberDialogProps {
  open: boolean;
  onClose: () => void;
  member: ITeamMember | null;
}

export function RemoveMemberDialog({ open, onClose, member }: RemoveMemberDialogProps) {
  const { t } = useTranslation('team');
  const queryClient = useQueryClient();

  const { mutateAsync: revokeMember, isPending } = useMutation({
    mutationFn: () => TeamService.revokeMember(member!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members'] });
      toast({ description: t('member_removed') });
      onClose();
    },
    onError: () => {
      toast({ variant: 'destructive', description: t('remove_error') });
    },
  });

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-2">
            <AlertTriangle className="w-6 h-6 text-red-500" />
          </div>
          <DialogTitle className="text-center">{t('remove_member_title')}</DialogTitle>
          <DialogDescription className="text-center">
            {t('remove_member_description', { name: member.name || member.email })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex gap-2 sm:justify-center">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => revokeMember()}
            disabled={isPending}
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin h-4 w-4" />
                {t('removing')}
              </span>
            ) : (
              t('remove')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
